import React, { useEffect, useState } from 'react';
import { Card, Container, Row } from 'react-bootstrap';
import { isEmpty } from 'lodash';
import DynamicForm from 'screens/common/dynamicForm';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import FalconCardHeader from 'components/common/FalconCardHeader';
import { useNavigate } from 'react-router-dom';
import { farmReportApi } from 'api/endpoints/farm';
import { useParams } from 'react-router-dom';
import { formSubmissionToast } from 'helpers/utils';
import { getTenantFromToken } from 'utils/routeUtils';
import offlineDBService from 'db/dexieDB';
import useOnlineStatus from 'hooks/useOnlineStatus';

function EditFarm() {
  const isOnline = useOnlineStatus();

  const tenant = getTenantFromToken();
  const tns_tenant = '03534add672340809e2c70314b1ce20e';
  const navigate = useNavigate();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [farmData, setFarmData] = useState(null);
  const { id } = useParams();
  const farmId = id;

  const populateFormWithFarmData = (formSchema, farmData) => {
    if (!formSchema || !formSchema.fields || !farmData) {
      console.error('Missing required data to populate form');
      return formSchema;
    }

    // Clone the form schema to avoid modifying the original
    const updatedForm = JSON.parse(JSON.stringify(formSchema));

    // Update the form fields with farm data
    updatedForm.fields = updatedForm.fields.map(field => {
      // Process the field even if there's no farm data for it
      // as we need to handle default values in the schema itself

      // First, let's fix any numeric defaultValue that's stored as a string in the field definition
      if (
        field.type === 'dropdown' ||
        field.type === 'select' ||
        field.type === 'number'
      ) {
        if (
          field.defaultValue !== undefined &&
          typeof field.defaultValue === 'string' &&
          !isNaN(field.defaultValue)
        ) {
          // This looks like a numeric value stored as a string in the field definition
          // console.log(
          //   `Converting default value for ${field.id} from "${
          //     field.defaultValue
          //   }" to number ${Number(field.defaultValue)}`
          // );
          field.defaultValue = Number(field.defaultValue);
        }
      }

      // Now get the value from farm data if it exists
      const fieldValue = farmData[field.id];

      // If no farm data for this field, return the field with potentially fixed defaultValue
      if (fieldValue === undefined) {
        return field;
      }

      // Handle location field (a special case for farms)
      if (field.id === 'location' && typeof fieldValue === 'object') {
        return {
          ...field,
          defaultValue: fieldValue
        };
      }
      // Handle coordinates field (another special case for farms)
      else if (field.id === 'coordinates' && fieldValue !== null) {
        return {
          ...field,
          defaultValue: fieldValue
        };
      }
      // Handle irrigation_source (could be an array)
      else if (field.id === 'irrigation_source' && Array.isArray(fieldValue)) {
        return {
          ...field,
          defaultValue: fieldValue
        };
      }
      // Handle table fields
      else if (field.table) {
        const tableData = Array.isArray(fieldValue) ? fieldValue : [];
        return {
          ...field,
          table: {
            ...field.table,
            rows: tableData.length > 0 ? tableData : field.table.rows
          }
        };
      }
      // Handle dropdown fields - ensure they are converted to numbers if needed
      else if (field.type === 'dropdown' || field.type === 'select') {
        let convertedValue = fieldValue;

        // Convert string to number if it looks like a number and the field doesn't expect strings
        // But avoid converting IDs that are UUIDs or similar text-based values
        if (
          typeof fieldValue === 'string' &&
          !isNaN(fieldValue) &&
          // Check if field has options and all values are numeric
          !(
            field.options &&
            field.options.length > 0 &&
            field.options.some(
              opt => typeof opt.value === 'string' && isNaN(opt.value)
            )
          )
        ) {
          convertedValue = Number(fieldValue);
          // console.log(
          //   `Converting field ${field.id} value from string "${fieldValue}" to number ${convertedValue}`
          // );
        }

        return {
          ...field,
          defaultValue: convertedValue
        };
      }
      // Handle numeric input fields
      else if (field.type === 'number') {
        let numValue = fieldValue;

        // Convert string to number if needed
        if (typeof fieldValue === 'string' && !isNaN(fieldValue)) {
          numValue = Number(fieldValue);
          // console.log(
          //   `Converting number field ${field.id} from string "${fieldValue}" to number ${numValue}`
          // );
        }

        return {
          ...field,
          defaultValue: numValue
        };
      }
      // Handle all other fields
      else {
        return {
          ...field,
          defaultValue: fieldValue
        };
      }
    });

    // Additional pass to normalize dependent dropdown fields default values
    // This is needed because some dropdowns might depend on other dropdowns
    updatedForm.fields.forEach(field => {
      if (
        (field.type === 'dropdown' || field.type === 'select') &&
        field.defaultValue !== undefined
      ) {
        // Ensure dropdown values are numbers if they look like numbers
        // but be careful not to convert string IDs that are supposed to be strings
        if (
          typeof field.defaultValue === 'string' &&
          !isNaN(field.defaultValue) &&
          // Check if there are options and all values are numeric
          !(
            field.options &&
            field.options.length > 0 &&
            field.options.some(
              opt => typeof opt.value === 'string' && isNaN(opt.value)
            )
          )
        ) {
          field.defaultValue = Number(field.defaultValue);
          console.log(
            `Final conversion for ${field.id} to number: ${field.defaultValue}`
          );
        }
      }
    });

    return updatedForm;
  };

  const fetchFormData = async () => {
    setLoading(true);
    try {
      let formSchema = null;
      let farmData = null;

      // Try to get farm from offline DB first
      try {
        farmData = await offlineDBService.getFarmById(
          parseInt(farmId, 10) || farmId
        );
        console.log('Farm data from offline DB:', farmData);
        setFarmData(farmData);
      } catch (dbError) {
        console.error('Error fetching farm from local DB:', dbError);
      }

      const isOfflineFarm = farmData && farmData.synced === false;
      const workingOffline = isOfflineFarm || !isOnline;

      if (isOnline && !isOfflineFarm) {
        // Online mode - get form from API
        let isEdit = true;
        const formResponse = await farmReportApi.getDynamicFarmForm(
          isEdit,
          farmId
        );
        formSchema = formResponse?.data?.results;
        setData(formSchema);
      } else {
        // Offline mode - get form schema from local DB
        const formDef = await offlineDBService.getFormDefinition('farm-create');

        if (formDef && formDef.formData) {
          console.log('Form definition found in local DB:', formDef.formData);
          formSchema = formDef.formData;

          // If we have offline farm data, populate the form with it
          if (farmData) {
            const populatedForm = populateFormWithFarmData(
              formSchema,
              farmData
            );
            setData(populatedForm);
          } else {
            setData(formSchema);
            toast.warning(
              'Could not find farm data. Some fields may be empty.'
            );
          }
        } else {
          console.error('No form definition found in local DB');
          toast.error('Could not load the form for offline editing.');
        }
      }

      // If we have farm data and it has a farmer ID, try to get farmer details
      if (farmData && farmData.farmer) {
        try {
          const associatedFarmer = await offlineDBService.getFarmerById(
            farmData.farmer
          );
        } catch (farmerError) {
          console.error('Error fetching associated farmer data:', farmerError);
        }
      }
    } catch (error) {
      console.error('Error fetching form data:', error);
      toast.error('Failed to load form. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFormData();
  }, []);

  const handleFormSubmit = async formData => {
    console.log('Form data submitted:', formData);
    try {
      // Check if user is online - if online, always try API method first
      if (isOnline) {
        console.log('User is online - proceeding with API submission');

        // Check if this farm exists in local DexieDB
        let localFarm = null;
        let isUnsynced = false;

        try {
          localFarm = await offlineDBService.getFarmById(
            parseInt(farmId, 10) || farmId
          );

          if (localFarm) {
            console.log('Found farm in local DB:', localFarm);
            // Check if the farm is unsynced (created offline)
            isUnsynced = localFarm.synced === false;

            if (isUnsynced) {
              console.log('This is an unsynced farm (created offline)');
            } else {
              console.log('This is a synced farm (from main DB)');
            }
          }
        } catch (dbError) {
          console.log(
            'No local farm found with this ID, assuming it exists only in main DB'
          );
        }

        try {
          let response;

          // EDGE CASE: If the farm exists locally and is unsynced (synced === false),
          // treat it as a new farm creation rather than an edit
          if (localFarm && isUnsynced) {
            console.log(
              'Creating new farm in main DB for this unsynced local farm'
            );

            // Check if the associated farmer is also unsynced and not yet submitted
            let farmerId = localFarm.farmer;
            let unsynced = false;

            if (farmerId) {
              try {
                const associatedFarmer = await offlineDBService.getFarmerById(
                  farmerId
                );
                if (associatedFarmer && associatedFarmer.synced === false) {
                  console.log(
                    'Farm belongs to an unsynced farmer. Make sure to sync the farmer first.'
                  );
                  unsynced = true;
                  toast.warning(
                    'This farm belongs to an unsynced farmer. Please sync the farmer first.'
                  );
                }
              } catch (farmerError) {
                console.error('Error checking associated farmer:', farmerError);
              }
            }

            // Prepare data - merge form data with original data to ensure all fields are present
            const dataToCreate = {
              ...localFarm, // Include all original data
              ...formData, // Override with new form data
              // Remove local DB specific fields that shouldn't go to API
              id: undefined, // Don't send local ID to API
              synced: undefined,
              syncAttempted: undefined,
              syncError: undefined
            };

            // Call create API
            if (!unsynced) {
              response = await farmReportApi.submitFarm(
                dataToCreate.farmer,
                dataToCreate
              );
            } else {
              // If the farmer is unsynced, save back to local DB with updates
              throw new Error(
                'Cannot submit farm with unsynced farmer. Please sync the farmer first.'
              );
            }
          } else {
            // Normal case: Edit an existing farm in the main DB
            console.log('Editing existing farm in main DB');
            response = await farmReportApi.submitEditFarm(farmId, formData);
          }

          console.log('API response:', response);

          // Check if the response indicates success
          const isSuccessful =
            response &&
            (response.data?.success === true ||
              (response.status &&
                response.status >= 200 &&
                response.status < 300));

          // If API save was successful and we have a local copy, remove it from DexieDB
          if (isSuccessful && localFarm) {
            try {
              console.log(
                `Attempting to delete local farm with ID ${localFarm.id}`
              );
              await offlineDBService.deleteFarm(localFarm.id);
              console.log(
                `Successfully removed farm with ID ${localFarm.id} from local DB`
              );
              toast.info('Data saved to server and removed from local storage');
            } catch (deleteError) {
              console.error('Error removing farm from local DB:', deleteError);
              toast.warning('Saved to server but failed to remove local copy');
            }
          } else if (isSuccessful) {
            // Regular success message
            toast.success('Farm data updated successfully');
          } else {
            // Some other response format, use the helper
            formSubmissionToast(response, toast, 'farm', true);
          }

          // Navigate back to the main page
          navigate(`/${tenant}/farmlandReport/`);
        } catch (apiError) {
          console.error('API error:', apiError);
          formSubmissionToast(apiError, toast, 'farm', true);

          // If online API call fails, we could optionally fall back to saving locally
          if (
            window.confirm(
              'Failed to save to server. Would you like to save locally instead?'
            )
          ) {
            saveLocally(formData);
          }
        }
      } else {
        // User is offline, save to local DB
        console.log('User is offline - saving to local DB');
        saveLocally(formData);
        toast.success('Farm data saved locally. Will sync when online.');
        navigate(`/offline-data/`);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      toast.error('An unexpected error occurred while updating the farm.');
    }
  };

  // Helper function to save data locally
  const saveLocally = async formData => {
    try {
      console.log(`Attempting to update farm with ID: ${farmId}`);

      // If we have the original farm data, merge it with the form data to ensure
      // we don't lose fields that weren't in the form
      let dataToUpdate = { ...formData };

      if (farmData) {
        console.log('Merging with existing farm data');
        // Start with original data and override with form data
        // This ensures we preserve fields that aren't in the form
        dataToUpdate = {
          ...farmData,
          ...formData,
          // Explicitly set these fields to ensure they're updated
          synced: false,
          updatedAt: new Date().toISOString()
        };
      }

      // Handle the case where the form data might have an 'id' field
      // that conflicts with the database ID
      if (dataToUpdate.id && dataToUpdate.id !== farmId) {
        console.log(
          `Form data has different ID (${dataToUpdate.id}) than farm ID (${farmId})`
        );
        // Save the form's ID field under a different name if needed
        dataToUpdate.formId = dataToUpdate.id;
      }

      // Set the ID to match the route parameter
      dataToUpdate.id = farmId;

      console.log('Final data to update:', dataToUpdate);

      try {
        const result = await offlineDBService.updateFarm(farmId, dataToUpdate);
        console.log('Update result:', result);

        toast.success('Farm data updated locally. Will sync when online.');
        navigate(`/offline-data/`);
      } catch (updateError) {
        // Show the error to the user directly instead of trying to create a new record
        console.error('Error updating farm:', updateError);
        toast.error('Failed to update farm: ' + updateError.message);

        // Stay on the form page so user can correct issues
        // Don't navigate away or create new records
      }
    } catch (error) {
      console.error('Error in saveLocally function:', error);
      toast.error('Failed to process farm data: ' + error.message);
    }
  };

  const header = (
    <div className="d-flex align-items-center">
      <div
        className="me-2 my-auto theme-control-toggle"
        onClick={() => navigate(`/${tenant}/farmlandReport`)}
      >
        <div className="theme-control-toggle-label">
          <FontAwesomeIcon icon={faArrowLeft} className="fs-0" />
        </div>
      </div>
      <span>Edit Farm Details</span>
    </div>
  );

  return (
    <Container>
      <Row>
        <Card className="mb-3">
          <Card.Header>
            <FalconCardHeader
              title={header}
              className="ps-lg-4"
              titleTag="h5"
              titleClass="mb-0"
              light
            />
          </Card.Header>
          <Card.Body>
            {loading ? (
              <div className="text-center p-5">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading farm data...</p>
              </div>
            ) : !isEmpty(data) ? (
              <DynamicForm
                data={data}
                header={true}
                onSubmit={handleFormSubmit}
                mode="edit"
              />
            ) : (
              <div className="text-center p-5">
                <p>No farm data available to edit.</p>
              </div>
            )}
          </Card.Body>
        </Card>
      </Row>
    </Container>
  );
}

export default EditFarm;
